import { useCallback } from 'react';
import { ConceptCode } from '@tiendanube/common';
import { combineStatus } from 'commons/utils/combineStatus';
import { usePlanDowngrade } from 'domains/Billing/BillingPlans/hooks';
import {
  useChargesToPay,
  useGetRecurrentPaymentConcepts,
  useGetSubscriptions,
} from 'domains/Billing/Checkout/hooks';

export function useSubscriptionDetail(
  concept: ConceptCode,
  externalReference?: string,
) {
  const {
    status: subscriptionsStatus,
    getSubscriptionForConcept,
    refreshSubscriptions,
  } = useGetSubscriptions();
  const {
    status: recurrentConceptsStatus,
    recurrentPaymentForConcept,
    refreshRecurrentPaymentConcepts,
  } = useGetRecurrentPaymentConcepts();

  const {
    isAvailableForPayment,
    status: chargesToPayStatus,
    refreshChargesToPay,
  } = useChargesToPay();

  const subscription = getSubscriptionForConcept(concept, externalReference);

  const {
    scheduledDowngrade,
    status: downgradeStatus,
    currentPlan,
    newPlan,
    retryLoad: refreshDowngradeData,
  } = usePlanDowngrade(concept === 'plan-cost');

  const { isLoading, isError, status, isSuccess } = combineStatus(
    subscriptionsStatus,
    recurrentConceptsStatus,
    chargesToPayStatus,
    downgradeStatus,
  );

  const retryLoad = useCallback(() => {
    refreshRecurrentPaymentConcepts();
    refreshSubscriptions();
    refreshChargesToPay();
    refreshDowngradeData();
  }, [
    refreshRecurrentPaymentConcepts,
    refreshSubscriptions,
    refreshChargesToPay,
    refreshDowngradeData,
  ]);

  const recurrentPaymentActivated = recurrentPaymentForConcept(
    concept,
    externalReference,
  );

  return {
    isLoading,
    isError,
    isSuccess,
    isAvailableForPayment,
    retryLoad,
    subscription,
    recurrentPaymentActivated,
    status,
    isDowngradeScheduled: !!scheduledDowngrade,
    currentPlan,
    newPlan,
  };
}
